import { FC } from 'react';
import { useHistory } from 'react-router-dom';
import Swal from 'sweetalert2';

import { useTranslate } from 'hooks/useTranslate';

import errorSwal from 'utils/errorSwal';
import successSwal from 'utils/successSwal';

import { store } from 'redux/store';

import { cancelTradingOffer } from 'modules/trading/redux/tradingOfferActions';
import { getTradingOfferManageActionsVisibility } from 'modules/trading/utils/canManageTradingOffer';

import { TradingOffer, TradingOfferType } from 'types/TradingOffer';
import Button, { ButtonVariants } from 'modules/_shared/components/Buttons';

type TradingOfferManageActionsProps = {
  offer: TradingOffer;
  societyId: string;
  isAdmin: boolean;
  userPartnerIds: string[];
  partnerQuerySuffix: string;
};

const TradingOfferManageActions: FC<TradingOfferManageActionsProps> = ({
  offer,
  societyId,
  isAdmin,
  userPartnerIds,
  partnerQuerySuffix,
}) => {
  const { t } = useTranslate();
  const history = useHistory();

  const { canModify, canCancel } = getTradingOfferManageActionsVisibility({
    offer,
    isAdmin,
    userPartnerIds,
  });

  if (!canModify && !canCancel) {
    return null;
  }

  const publisherPartnerId =
    typeof offer.partner === 'string'
      ? offer.partner
      : offer.partner?._id || '';

  const partnerEditQuery = publisherPartnerId
    ? `?partnerId=${publisherPartnerId}`
    : '';

  const handleModify = () => {
    if (offer.type === TradingOfferType.SELL) {
      history.push(
        `/editar-oferta-venta/${societyId}/${offer._id}${partnerEditQuery}`
      );
      return;
    }
    history.push(
      `/editar-oferta-compra/${societyId}/${offer._id}${partnerEditQuery}`
    );
  };

  const handleCancelOffer = () => {
    Swal.fire({
      icon: 'warning',
      title: t('CancelOfferConfirmationTitle'),
      text: t('CancelOfferConfirmation'),
      showCancelButton: true,
      confirmButtonText: t('Confirm'),
      cancelButtonText: t('Cancel'),
      confirmButtonColor: '#6576ff',
    }).then(async (result) => {
      if (!result.isConfirmed) {
        return;
      }
      try {
        await store.dispatch(cancelTradingOffer(offer._id));
        successSwal(t('OfferCanceledSuccessfully'));
        history.push(`/ofertas/${societyId}${partnerQuerySuffix}`);
      } catch {
        errorSwal(t('ErrorCancelingOffer'));
      }
    });
  };

  return (
    <>
      {canModify && (
        <li>
          <Button
            variant={ButtonVariants.GRAY}
            data-testid="trading-offer-modify-button"
            onClick={handleModify}
          >
            {t('ModifyOffer')}
          </Button>
        </li>
      )}
      {canCancel && (
        <li>
          <Button
            variant={ButtonVariants.DANGER}
            data-testid="trading-offer-cancel-button"
            onClick={handleCancelOffer}
          >
            <em className="icon ni ni-cross" />
            <span className="ml-1">{t('CancelOffer')}</span>
          </Button>
        </li>
      )}
    </>
  );
};

export default TradingOfferManageActions;
